import React, { useState, useEffect } from 'react';
import { Download, RefreshCw } from 'lucide-react';

interface UpdateBadgeProps {
  onClick?: () => void;
  className?: string;
}

export function UpdateBadge({ onClick, className = '' }: UpdateBadgeProps) {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    const checkForUpdates = async () => {
      if ('serviceWorker' in navigator) {
        try {
          const registration = await navigator.serviceWorker.ready;

          // Check if there's a waiting service worker
          if (registration.waiting) {
            setUpdateAvailable(true);
          }

          // Listen for new service worker installing
          registration.addEventListener('updatefound', () => {
            const newWorker = registration.installing;
            if (newWorker) {
              newWorker.addEventListener('statechange', () => {
                if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                  setUpdateAvailable(true);
                }
              });
            }
          });

          // Check for updates periodically
          const interval = setInterval(() => {
            if (!document.hidden) {
              registration.update();
            }
          }, 5 * 60 * 1000);

          return () => clearInterval(interval);
        } catch (error) {
          console.error('Error checking for updates:', error);
        }
      }
    };

    checkForUpdates();
  }, []);

  const handleUpdateClick = async () => {
    setUpdating(true);
    if (onClick) {
      onClick();
    }
  };

  if (!updateAvailable) {
    return null;
  }

  return (
    <button
      onClick={handleUpdateClick}
      disabled={updating}
      className={`relative p-2 rounded-full hover:bg-gray-100 transition-colors group ${className}`}
      title="تحديث متاح - اضغط للتحديث"
    >
      {updating ? (
        <RefreshCw className="w-5 h-5 text-blue-600 animate-spin" />
      ) : (
        <Download className="w-5 h-5 text-blue-600 animate-bounce" />
      )}
      
      {/* Animated notification dot */}
      <div className="absolute -top-1 -right-1 w-3 h-3 bg-gradient-to-r from-red-400 to-red-600 rounded-full">
        <div className="absolute inset-0 bg-red-500 rounded-full animate-ping opacity-75"></div>
        <div className="relative w-full h-full bg-gradient-to-r from-red-400 to-red-600 rounded-full"></div>
      </div>

      {/* Tooltip on hover */}
      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-1 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
        {updating ? 'جاري التحديث...' : 'إصدار جديد متاح'}
        <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-2 h-2 bg-gray-800 rotate-45"></div>
      </div>
    </button>
  );
}